/**
 * Query provider singleton, and the one client every page shares.
 *
 * Bond packages call {@link setProvider} during application startup.
 * Application code calls {@link getQueryClient} at runtime.
 *
 * @module
 */
import { bond, get as bondGet, isBonded } from '@molecule/app-bond';
/** Bond category key for the query provider. */
const BOND_TYPE = 'query';
let client = null;
let clientConfig;
/**
 * Registers a query provider as the active singleton. Called by bond
 * packages (e.g. `@molecule/app-query-tanstack`) during app startup.
 *
 * @param provider - The query provider implementation to bond.
 */
export function setProvider(provider) {
    bond(BOND_TYPE, provider);
    client = null;
}
/**
 * Retrieves the bonded query provider, throwing if none is configured.
 *
 * @returns The bonded query provider.
 * @throws {Error} If no query provider has been bonded.
 */
export function getProvider() {
    const provider = bondGet(BOND_TYPE);
    if (!provider) {
        throw new Error('@molecule/app-query: No provider bonded. Call setProvider() with a query bond (e.g. @molecule/app-query-tanstack).');
    }
    return provider;
}
/**
 * Checks whether a query provider is currently bonded.
 *
 * @returns `true` if a query provider is bonded.
 */
export function hasProvider() {
    return isBonded(BOND_TYPE);
}
/**
 * Sets the defaults the shared client is created with. Call before the first
 * {@link getQueryClient}; later calls take effect after {@link resetQueryClient}.
 *
 * @param config - Defaults for every query.
 */
export function configureQueryClient(config) {
    clientConfig = config;
}
/**
 * The app's one cache, created by the bonded provider on first use and
 * reused afterwards, so every page reads and warms the same documents.
 *
 * @returns The shared client.
 * @throws {Error} If no query provider has been bonded.
 */
export function getQueryClient() {
    if (!client)
        client = getProvider().createClient(clientConfig);
    return client;
}
/**
 * Forgets the shared client (its next use creates a new one). Tests only.
 */
export function resetQueryClient() {
    client?.clear();
    client = null;
}
